/**
 * GlobalBanner Component
 *
 * Displays system-wide banner notifications from the admin.
 * Fetches active banners for the current organization on mount.
 * Supports info, warning, and alert severity levels.
 */

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';
import { supabase } from '../utils/supabase/client';

interface SystemBanner {
  id: string;
  message: string;
  level: 'info' | 'warning' | 'alert';
  scope: 'all' | 'specific_orgs';
  target_org_ids: string[];
}

const DISMISSED_KEY = 'adcanvas_dismissed_banners';

const getDismissedBanners = (): string[] => {
  try {
    const stored = sessionStorage.getItem(DISMISSED_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const setDismissedBanner = (id: string) => {
  try {
    const dismissed = getDismissedBanners();
    if (!dismissed.includes(id)) {
      dismissed.push(id);
      sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
    }
  } catch {
    // Ignore storage errors
  }
};

export function GlobalBanner() {
  const { currentOrganization } = useOrganization();
  const [banners, setBanners] = useState<SystemBanner[]>([]);
  // Read sessionStorage in a lazy initialiser rather than an effect. The old form
  // rendered once with [] and then immediately set state, so a dismissed banner
  // flashed visible for one frame before disappearing. The initialiser runs once,
  // before first paint, and getDismissedBanners already swallows storage errors.
  const [dismissedIds, setDismissedIds] = useState<string[]>(getDismissedBanners);

  // Directly offset fixed sidebar when banner is visible
  useEffect(() => {
    const visibleCount = banners.filter(b => !dismissedIds.includes(b.id)).length;
    const bannerHeight = '28px';

    // Find and offset the fixed sidebar container
    const sidebarContainer = document.querySelector('[data-slot="sidebar-container"]') as HTMLElement;

    if (visibleCount > 0) {
      if (sidebarContainer) {
        sidebarContainer.style.top = bannerHeight;
        sidebarContainer.style.height = `calc(100svh - ${bannerHeight})`;
      }
    } else {
      if (sidebarContainer) {
        sidebarContainer.style.top = '';
        sidebarContainer.style.height = '';
      }
    }

    return () => {
      if (sidebarContainer) {
        sidebarContainer.style.top = '';
        sidebarContainer.style.height = '';
      }
    };
  }, [banners, dismissedIds]);

  // Fetch active banners when org changes
  useEffect(() => {
    const fetchBanners = async () => {
      if (!currentOrganization?.id) return;

      try {
        const { data, error } = await supabase
          .from('system_banners')
          .select('id, message, level, scope, target_org_ids')
          .eq('is_active', true);

        if (error) {
          console.error('Error fetching banners:', error);
          return;
        }

        // Filter banners by scope
        const relevantBanners = (data || []).filter((banner: SystemBanner) => {
          if (banner.scope === 'all') return true;
          if (banner.scope === 'specific_orgs' && banner.target_org_ids) {
            return banner.target_org_ids.includes(currentOrganization.id);
          }
          return false;
        });

        setBanners(relevantBanners);
      } catch (err) {
        console.error('Error fetching banners:', err);
      }
    };

    fetchBanners();
  }, [currentOrganization?.id]);

  const handleDismiss = (id: string) => {
    setDismissedBanner(id);
    setDismissedIds(prev => [...prev, id]);
  };

  // Filter out dismissed banners
  const visibleBanners = banners.filter(b => !dismissedIds.includes(b.id));

  if (visibleBanners.length === 0) {
    return null;
  }

  return (
    <div className="w-full flex flex-col shrink-0 z-50 relative">
      {visibleBanners.map(banner => (
        <BannerItem
          key={banner.id}
          banner={banner}
          onDismiss={() => handleDismiss(banner.id)}
        />
      ))}
    </div>
  );
}

interface BannerItemProps {
  banner: SystemBanner;
  onDismiss: () => void;
}

function BannerItem({ banner, onDismiss }: BannerItemProps) {
  // Supabase-style compact banner with muted colors
  const levelConfig = {
    info: {
      dotColor: '#3b82f6', // blue-500
      bgColor: '#f8fafc',
      borderColor: '#e2e8f0',
    },
    warning: {
      dotColor: '#f59e0b', // amber-500
      bgColor: '#fffbeb',
      borderColor: '#fde68a',
    },
    alert: {
      dotColor: '#ef4444', // red-500
      bgColor: '#fef2f2',
      borderColor: '#fecaca',
    },
  };

  const config = levelConfig[banner.level];

  return (
    <div
      role="alert"
      style={{
        backgroundColor: config.bgColor,
        borderBottom: `1px solid ${config.borderColor}`,
      }}
      className="flex items-center justify-center gap-2 px-4 py-1.5 text-xs w-full"
    >
      {/* Status dot indicator like Supabase */}
      <span
        className="shrink-0 h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: config.dotColor }}
      />
      <span className="text-foreground/70">{banner.message}</span>
      <button
        onClick={onDismiss}
        className="shrink-0 ml-1 p-0.5 hover:bg-black/5 transition-colors rounded text-muted-foreground/60 hover:text-muted-foreground"
        aria-label="Dismiss banner"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
